import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';
import { PaymentForm } from '@/components/payments/payment-form';
import { useOrder } from '@/hooks/use-orders';
import { useCreatePayment } from '@/hooks/use-payments';
import { useToast } from '@/hooks/use-toast';
import type { PaymentFormData } from '@/components/payments/payment-form';

export const Route = createFileRoute('/payments/new/$orderId')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <NewPaymentPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
}

function NewPaymentPage() {
  const { orderId } = useParams({ from: '/payments/new/$orderId' });
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: order, isLoading, error } = useOrder(orderId);
  const createPayment = useCreatePayment();

  const handleSubmit = async (data: PaymentFormData) => {
    try {
      await createPayment.mutateAsync({
        orderId: orderId,
        paymentDate: new Date().toISOString().split('T')[0],
        amount: data.amount,
        paymentMethod: data.paymentMethod,
        notes: data.notes,
      });
      toast({
        title: 'Payment Processed',
        description: 'Payment has been recorded successfully.',
      });
      navigate({ to: '/orders/$id', params: { id: orderId } });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to process payment. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center py-24 text-destructive">
        <p>Failed to load order details.</p>
        <Link to="/orders">
          <Button variant="outline" className="mt-4">Back to Orders</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <PageHeader
        title="Process Payment"
        description={`Order ${order.orderNumber}`}
        actions={(
          <Link to="/orders/$id" params={{ id: orderId }}>
            <Button variant="outline" className="w-full sm:w-auto">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Order
            </Button>
          </Link>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Order Summary</CardTitle>
          <CardDescription>
            {order.customer.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <p className="text-muted-foreground">Total Amount</p>
              <p className="text-lg font-bold">{formatCurrency(order.totalAmount)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Payment Status</p>
              <Badge
                variant={
                  order.paymentStatus === 'PAID'
                    ? 'default'
                    : order.paymentStatus === 'PARTIAL'
                      ? 'secondary'
                      : 'destructive'
                }
              >
                {order.paymentStatus}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <PaymentForm
        order={order}
        onSubmit={handleSubmit}
        isSubmitting={createPayment.isPending}
        onCancel={() => navigate({ to: '/orders/$id', params: { id: orderId } })}
      />
    </div>
  );
}
